import { Controller } from "@hotwired/stimulus"

// The stock mix picker (plan 009 U5: R5, R9, KTD5). The Stocks basket's
// dollars split evenly across the ticked stocks; this keeps the sentence
// under the boxes true as the investor ticks, and stops a tick that would
// leave a stock under the floor — the server refuses the same pick, this
// just says so before the round trip.
//
// The dollars come from the amount and Stocks-share inputs when the picker
// sits on the plan form, and from values when it sits on the plan status
// page, where both are fixed.
export default class extends Controller {
  static targets = ["box", "split", "note", "amount", "stocksPercent"]
  static values = { amount: Number, stocksPercent: Number, floor: { type: Number, default: 10 } }

  connect() {
    this.refresh()
  }

  refresh() {
    const dollars = this.stocksDollars()
    const ticked = this.boxTargets.filter((box) => box.checked)
    const capacity = Math.floor(dollars / this.floorValue)
    const full = dollars > 0 && ticked.length >= capacity

    this.boxTargets.forEach((box) => {
      if (!box.checked) box.disabled = full
    })
    // At capacity the note explains the dimmed boxes; over it (the amount
    // was lowered under an existing mix) it is the refusal the save will get.
    if (this.hasNoteTarget) {
      const over = dollars > 0 && ticked.length > capacity
      this.noteTarget.hidden = !(full || over)
      this.noteTarget.classList.toggle("text-paper-caution", over)
      this.noteTarget.classList.toggle("text-paper-muted", !over)
    }
    // Every copy of the sentence: under the picker, and read back on the
    // builder's last step.
    const sentence = this.sentence(dollars, ticked)
    this.splitTargets.forEach((target) => { target.textContent = sentence })
  }

  sentence(dollars, ticked) {
    if (dollars <= 0) return "Nothing goes to Stocks right now."
    if (ticked.length === 0) return "Pick at least one stock for your Stocks basket."

    const names = ticked.map((box) => box.dataset.name)
    if (ticked.length === 1) return `Your ${this.currency(dollars)} in Stocks buys ${names[0]}.`

    const each = this.currency(dollars / ticked.length)
    return `Your ${this.currency(dollars)} in Stocks splits evenly: ${this.list(names.map((name) => `${each} ${name}`))}.`
  }

  stocksDollars() {
    const amount = this.hasAmountTarget ? Number(this.amountTarget.value) : this.amountValue
    const percent = this.hasStocksPercentTarget ? Number(this.stocksPercentTarget.value) : this.stocksPercentValue
    if (!Number.isFinite(amount) || !Number.isFinite(percent)) return 0
    return Math.max(amount, 0) * Math.min(Math.max(percent, 0), 100) / 100
  }

  list(items) {
    if (items.length <= 2) return items.join(" and ")
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
  }

  currency(value) {
    const cents = Math.floor(value * 100) / 100
    return cents.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: Number.isInteger(cents) ? 0 : 2,
      maximumFractionDigits: 2
    })
  }
}
